import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import { formatMoney } from "@/lib/currency";

export default function EnquireDialog({ open, onClose, item, stripeEnabled }) {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!open || !item) return null;

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { item_id: item.id, item_title: item.title, name, email, phone, message });
      toast.success("enquiry sent — the atelier will respond shortly.");
      setName(""); setEmail(""); setPhone(""); setMessage("");
      onClose();
    } catch {
      toast.error("could not send enquiry.");
    } finally { setSending(false); }
  };

  const goToCheckout = () => {
    onClose();
    nav(`/checkout/${item.id}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="enquire-dialog">
      <div className="bg-ivory border border-taupe max-w-xl w-full p-10 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-taupe hover:text-charcoal" data-testid="enquire-close">
          <X className="w-5 h-5" />
        </button>
        <span className="editorial-label">acquire · enquire</span>
        <h2 className="font-serif text-4xl lowercase text-charcoal mt-3 mb-2">{item.title}</h2>
        <p className="text-sm text-espresso mb-8">{formatMoney(item.sale_price)} · {item.brand || "—"}</p>

        <form onSubmit={submitEnquiry} className="space-y-5">
          <div>
            <label className="editorial-label">your name</label>
            <input data-testid="enq-name" required value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
          </div>
          <div>
            <label className="editorial-label">email</label>
            <input data-testid="enq-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
          </div>
          <div>
            <label className="editorial-label">phone (optional)</label>
            <input data-testid="enq-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
          </div>
          <div>
            <label className="editorial-label">message</label>
            <textarea data-testid="enq-message" required rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="tell us what you'd like to know…" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="submit" disabled={sending} data-testid="enq-send" className="flex-1 bg-brass hover:bg-espresso text-ivory py-3 text-xs tracking-[0.25em] uppercase transition-colors disabled:opacity-50">
              {sending ? "sending…" : "send enquiry"}
            </button>
            <button type="button" onClick={goToCheckout} data-testid="enq-buy" className="flex-1 border border-brass text-brass hover:bg-brass hover:text-ivory py-3 text-xs tracking-[0.25em] uppercase transition-colors">
              acquire · {formatMoney(item.sale_price)}
            </button>
          </div>
          <p className="text-[0.65rem] text-taupe text-center tracking-widest uppercase mt-2">
            {stripeEnabled ? "secured by stripe" : "checkout in test mode · mock payment"}
          </p>
        </form>
      </div>
    </div>
  );
}
